import { FormikProvider } from 'formik';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';
import { Modal } from '../../../components';
import type { Device, Installer } from '../../../model';
import { useForm } from './form';
import { InstallerControl } from './installer-control';

interface Props {
  show: boolean;
  onClose: () => void;
  installers: Installer[];
  selectedDevices: Device[];
}

const Intro = styled.p`
  margin: 2px 2px 6px;
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.55;
  color: rgba(176, 186, 209, 0.72);
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
`;

const Button = styled.button<{ $variant: 'primary' | 'secondary' }>`
  border-radius: 9px;
  font-weight: 600;
  font-size: 13px;
  padding: 9px 18px;
  cursor: pointer;
  transition: all var(--transition);

  ${({ $variant }): string =>
    $variant === 'secondary'
      ? `
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.12);
        color: rgba(213, 221, 238, 0.92);
      `
      : `
        background: rgba(78, 95, 253, 0.16);
        border: 1px solid rgba(120, 130, 255, 0.4);
        color: rgb(159, 176, 255);
      `}

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }

  &:hover:not(:disabled),
  &:focus {
    outline: none;
    filter: brightness(1.1);
  }
`;

export const UpdateModal = ({
  show,
  onClose,
  installers,
  selectedDevices
}: Props): ReactElement => {
  const { formik, loading } = useForm({ locations: selectedDevices, installers, onClose });

  const { lcs, ems, updater } = useMemo(
    () =>
      installers.reduce<{ ems: Installer[]; lcs: Installer[]; updater: Installer[] }>(
        (prev, actual) => {
          if (actual.software === 'LCS') {
            prev.lcs.push(actual);
          }

          if (actual.software === 'EMS') {
            prev.ems.push(actual);
          }

          if (actual.software === 'Updater') {
            prev.updater.push(actual);
          }

          return prev;
        },
        { ems: [], lcs: [], updater: [] }
      ),
    [installers]
  );

  const scheduleUpdate = (updateNow: boolean) => (): void => {
    formik.setFieldValue('updateNow', updateNow);
    formik.handleSubmit();
  };

  return (
    <Modal
      open={show}
      onClose={onClose}
      title='Update Panel'
      width={560}
      footer={
        <Actions>
          <Button type='button' $variant='secondary' disabled={loading} onClick={scheduleUpdate(false)}>
            Update at 4 AM
          </Button>
          <Button type='button' $variant='primary' disabled={loading} onClick={scheduleUpdate(true)}>
            {loading ? 'Updating…' : 'Update Now'}
          </Button>
        </Actions>
      }
    >
      <Intro>
        Select the software and version to update all selected encoders. You can perform all three
        updates at once.
      </Intro>

      <FormikProvider value={formik}>
        <Fields>
          <InstallerControl
            name='lcs'
            nameSelect='lcsVersion'
            installers={lcs}
            label='Update LCS'
            error={formik.errors.lcsVersion}
          />
          <InstallerControl
            name='ems'
            nameSelect='emsVersion'
            installers={ems}
            label='Update EMS'
            error={formik.errors.emsVersion}
          />
          <InstallerControl
            name='updater'
            nameSelect='updaterVersion'
            installers={updater}
            label='Update Installer'
            error={formik.errors.updaterVersion}
          />
        </Fields>
      </FormikProvider>
    </Modal>
  );
};
